import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import FolderIcon from '@mui/icons-material/Folder';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useVideoProcessing } from '../../contexts/VideoProcessingContext';
import videoUploadServiceInstance from '../../services/VideoUploadService';
import { validateItemName } from '../../utils/formatUtils';
import { uploadVideoDialogHelpItems } from '../../utils/helpItems';
import HelpOverlay from '../common/HelpOverlay';
import PathSelectorDialog from '../dialogs/PathSelectorDialog';
import { SelectLanguageComponent } from './SelectLanguageComponent';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import { carryLanguages, contentFieldsError } from '../tasks/taskFields';
import TaskFieldsSection, { emptyTaskFields } from '../tasks/TaskFieldsSection';

const UploadVideoDialog = ({
    fileName,
    onFileSelect,
    linkType,
    handleLinkTypeSelect,
    setLinkError,
    open,
    handleClose,
    videoName,
    setVideoName,
    googleLinkURL,
    setGoogleLinkURL,
    youtubeLinkURL,
    setYoutubeLinkURL,
    isLinkError,
    getLanguagesForCreateVideo,
    isCompressed,
    setIsCompressed,
    selectOpenAI,
    setSelectOpenAI,
    isLogging,
    setIsLogging,
    skipTranslationIsChecked,
    setSkipTranslationIsChecked,
    onlyTransliterationIsChecked,
    setOnlyTransliterationIsChecked,
    bitRate,
    setBitRate,
    crfValue,
    setCRFValue,
    uploadVideo,
    setOpen,
    folderPath,
    darkMode,
    themeVariant,
}) => {
    useTrackUsage('UploadVideoDialog', 'dialog', 'subtitles', open);
    const [isFileSelected, setIsFileSelected] = useState(false);
    const [videoNameError, setVideoNameError] = useState(null);
    const [helpOpen, setHelpOpen] = useState(false);
    const [pathSelectorOpen, setPathSelectorOpen] = useState(false);
    const [selectedPath, setSelectedPath] = useState(folderPath || '/');

    // New states for drive link validation
    const [checkingDriveLink, setCheckingDriveLink] = useState(false);
    const [driveLinkVerified, setDriveLinkVerified] = useState(false);
    const [driveLinkErrorMessage, setDriveLinkErrorMessage] = useState('');
    const [subtitleFile, setSubtitleFile] = useState(null);

    // Get the tracking function from context
    const { trackVideo } = useVideoProcessing();

    useEffect(() => {
        //console.log("Setting up video tracking function");
        videoUploadServiceInstance.setTrackingFunction(trackVideo);

        // Initialize path from props when dialog opens
        if (open && folderPath) {
            setSelectedPath(folderPath);
        }

        return () => {
            videoUploadServiceInstance.setTrackingFunction(null);
        };
    }, [trackVideo, open, folderPath]);

    // Reset drive link verification when URL changes
    useEffect(() => {
        if (googleLinkURL) {
            setDriveLinkVerified(false);
            setDriveLinkErrorMessage('');
        }
    }, [googleLinkURL]);

    // Validate name when component mounts or videoName prop changes
    useEffect(() => {
        if (videoName) {
            const error = validateItemName(videoName, 'video');
            setVideoNameError(error);
        }
    }, [videoName]);

    // Add this new useEffect to clear subtitle file when dialog closes
    useEffect(() => {
        if (!open) {
            // Clear subtitle file when dialog closes
            setSubtitleFile(null);
            // The language picker starts over when the dialog reopens, so tracking does too
            setTaskFields(emptyTaskFields());
        }
    }, [open]);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        setIsFileSelected(!!file);

        if (file) {
            onFileSelect(file);
            handleLinkTypeSelect('local');

            // Extract filename without extension and set as video name
            const fileName = file.name;
            const nameWithoutExtension = fileName.replace(/\.[^/.]+$/, '');
            setVideoName(nameWithoutExtension);

            // Clear any existing validation errors since we're setting a new name
            setVideoNameError(null);
        }
    };

    const [taskFields, setTaskFields] = useState(emptyTaskFields());
    const [videoLanguages, setVideoLanguages] = useState([]);

    // Before tracking is first turned on there's nothing to update: the checkbox fills the list
    const handleVideoLanguages = (names) => {
        setVideoLanguages(names);
        setTaskFields((fields) =>
            fields.track || fields.languages.length > 0
                ? { ...fields, languages: carryLanguages(fields.languages, videoLanguages, names) }
                : fields,
        );
    };

    const handleVideoUpload = async () => {
        const nameError = validateItemName(videoName, 'video');

        if (nameError) {
            setVideoNameError(nameError);
            return;
        }

        // If name is valid, trim any extra spaces before uploading
        const trimmedName = videoName.trim();
        if (trimmedName !== videoName) {
            setVideoName(trimmedName);
        }

        // If it's a Google Drive link and not verified, verify it first
        if (linkType === 'gdrive' && !driveLinkVerified) {
            const isAccessible = await verifyDriveLink();
            if (!isAccessible) {
                return;
            }
        }

        // Call the upload function from props with the selected path
        const fields = taskFields;
        setTaskFields(emptyTaskFields());
        return uploadVideo(linkType, selectedPath, subtitleFile, fields);
    };

    const handlePathSelected = (path) => {
        setSelectedPath(path);
        setPathSelectorOpen(false);
    };

    const formatPath = (path) => {
        if (!path) return 'Home';
        if (path === '/') return 'Home';
        return path;
    };

    // Function to verify Google Drive link accessibility
    const verifyDriveLink = async () => {
        if (!googleLinkURL.trim()) {
            setDriveLinkErrorMessage('Please enter a Google Drive link');
            setLinkError(true);
            return false;
        }

        setCheckingDriveLink(true);

        try {
            // Simple HEAD request to check if the drive link is accessible
            const response = await axios.head(googleLinkURL, {
                timeout: 5000, // 5-second timeout
            });

            // If we get a 200 response, the link is accessible
            if (response.status === 200) {
                setDriveLinkVerified(true);
                setLinkError(false);
                setDriveLinkErrorMessage('');
                return true;
            } else {
                throw new Error('Link returned non-200 status');
            }
        } catch (error) {
            console.error('Error verifying drive link:', error);
            setDriveLinkVerified(false);
            setLinkError(true);
            setDriveLinkErrorMessage("This link appears to be restricted. Please make sure it's publicly accessible.");
            return false;
        } finally {
            setCheckingDriveLink(false);
        }
    };

    // Update the SRT file change handler
    const handleSubtitleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Check if file extension is supported
            const fileExtension = file.name.split('.').pop().toLowerCase();
            if (fileExtension !== 'srt' && fileExtension !== 'vtt') {
                alert('Unsupported file format. Please upload an SRT or VTT subtitle file');
                e.target.value = '';
                setSubtitleFile(null);
                return;
            }

            // Check file size (optional - limit to 10MB)
            if (file.size > 10 * 1024 * 1024) {
                alert('File too large. Please select a file smaller than 10MB');
                e.target.value = '';
                setSubtitleFile(null);
                return;
            }

            setSubtitleFile(file);
            console.log('Subtitle file selected:', file.name, 'Size:', file.size, 'bytes');
        } else {
            setSubtitleFile(null);
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: { borderRadius: 2 },
                }}
                data-testid="upload-video-dialog"
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" component="div" data-testid="upload-video-dialog-title">
                            Upload Video
                        </Typography>
                        <Tooltip title="Help Guide">
                            <IconButton size="small" onClick={() => setHelpOpen(true)}>
                                <HelpOutlineIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Please complete the form and click Upload to begin the processing.
                    </Typography>

                    {/* Video Name field with validation */}
                    <TextField
                        id="videoNameInput"
                        label="Video Name"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={videoName}
                        onChange={(e) => {
                            const newName = e.target.value;
                            setVideoName(newName);

                            const error = validateItemName(newName, 'video');
                            setVideoNameError(error);
                        }}
                        onBlur={() => {
                            const error = validateItemName(videoName, 'video');
                            setVideoNameError(error);
                        }}
                        required
                        error={!!videoNameError}
                        helperText={videoNameError || ''}
                        data-testid="video-name-input"
                    />

                    {/* Destination Path Selector */}
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Destination Folder:
                        </Typography>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderRadius: 1,
                            }}
                            data-testid="destination-folder-display"
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <FolderIcon color="primary" sx={{ mr: 1 }} />
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                    data-testid="selected-path-display"
                                >
                                    {formatPath(selectedPath)}
                                </Typography>
                            </Box>
                            <Tooltip title="Change destination folder">
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => setPathSelectorOpen(true)}
                                    aria-label="Change folder"
                                    data-testid="change-destination-btn"
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Paper>
                    </Box>

                    {/* Video Source Options */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 0.5 }}>Video Source</Typography>
                    <Paper id="upload-video-source" variant="outlined" sx={{ p: 2, borderRadius: 1 }} data-testid="video-source-options">
                        <Grid container spacing={2} direction="column">
                            {/* Google Drive Option with verification */}
                            <Grid item>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <TextField
                                        id="videoDriveURLInput"
                                        label="Google Drive Link"
                                        variant="outlined"
                                        fullWidth
                                        value={googleLinkURL}
                                        onChange={(e) => {
                                            setGoogleLinkURL(e.target.value);
                                            handleLinkTypeSelect('gdrive');
                                            setLinkError(false);
                                            setDriveLinkVerified(false);
                                        }}
                                        onBlur={(e) => {
                                            // Get the current value directly from the event
                                            const currentValue = e.target.value;

                                            // Only verify if there's actually a value and it's not already verified
                                            if (
                                                currentValue &&
                                                currentValue.trim() !== '' &&
                                                !driveLinkVerified &&
                                                !checkingDriveLink
                                            ) {
                                                // Use a ref to store the timeout ID
                                                const timeoutId = setTimeout(() => {
                                                    verifyDriveLink();
                                                }, 300);

                                                // Clean up timeout if component unmounts
                                                return () => clearTimeout(timeoutId);
                                            }
                                        }}
                                        error={isLinkError}
                                        InputProps={{
                                            endAdornment: driveLinkVerified ? (
                                                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                                            ) : null,
                                        }}
                                        data-testid="google-drive-input"
                                    />
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        disabled={!googleLinkURL || checkingDriveLink}
                                        onClick={verifyDriveLink}
                                        sx={{ minWidth: '120px', height: '56px' }}
                                        data-testid="verify-drive-link-btn"
                                    >
                                        {checkingDriveLink ? <CircularProgress size={24} /> : 'Check Access'}
                                    </Button>
                                </Box>
                                {googleLinkURL && driveLinkErrorMessage && (
                                    <Typography
                                        variant="caption"
                                        color="error"
                                        sx={{ display: 'block', mt: 0.5 }}
                                        data-testid="drive-link-error"
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <ErrorIcon fontSize="small" />
                                            {driveLinkErrorMessage}
                                        </Box>
                                    </Typography>
                                )}
                                {driveLinkVerified && (
                                    <Typography
                                        variant="caption"
                                        color="success.main"
                                        sx={{ display: 'block', mt: 0.5 }}
                                        data-testid="drive-link-success"
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <CheckCircleIcon fontSize="small" />
                                            Link is accessible and ready for processing
                                        </Box>
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Divider sx={{ flexGrow: 1 }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                                        or
                                    </Typography>
                                    <Divider sx={{ flexGrow: 1 }} />
                                </Box>
                            </Grid>

                            {/* YouTube Option */}
                            <Grid item>
                                <TextField
                                    id="youtubeURLInput"
                                    label="YouTube Link"
                                    variant="outlined"
                                    fullWidth
                                    placeholder="Coming soon - not fully functional right now"
                                    value={youtubeLinkURL}
                                    onChange={(e) => {
                                        setYoutubeLinkURL(e.target.value);
                                        handleLinkTypeSelect('youtube');
                                        setLinkError(false);
                                    }}
                                    data-testid="youtube-input"
                                />
                            </Grid>

                            <Grid item>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Divider sx={{ flexGrow: 1 }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                                        or
                                    </Typography>
                                    <Divider sx={{ flexGrow: 1 }} />
                                </Box>
                            </Grid>

                            {/* Local File Option */}
                            <Grid item>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    fullWidth
                                    sx={{ py: 1.5, textTransform: 'none' }}
                                    data-testid="local-file-upload-btn"
                                >
                                    {isFileSelected ? 'Change Selected File' : 'Upload Local File'}
                                    <input
                                        id="localFileInput"
                                        type="file"
                                        accept="video/*, audio/*"
                                        hidden
                                        onChange={handleFileChange}
                                        data-testid="local-file-input"
                                    />
                                </Button>
                                {isFileSelected && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', mt: 1 }}
                                        data-testid="selected-file-name"
                                    >
                                        Selected: {fileName?.name || 'File selected'}
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                    </Paper>

                    {isLinkError && !driveLinkErrorMessage && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }} data-testid="link-error-message">
                            The link is not publicly accessible.
                        </Typography>
                    )}

                    {/* Language Selection */}
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mt: 3, mb: 1 }}
                        data-testid="language-selection-title"
                    >
                        Language Selection
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select languages for subtitle generation. Basic languages are shown first for quick access.
                    </Typography>
                    <Box id="upload-language-selection" sx={{ my: 2 }} data-testid="language-selection-container">
                        <SelectLanguageComponent
                            id="selectLanguageDropdown"
                            disabled={false}
                            languageOptions={getLanguagesForCreateVideo}
                            darkMode={darkMode}
                            themeVariant={themeVariant}
                            isDarkMode = {darkMode === true}
                            showGrouped={true}
                            onLanguagesChange={handleVideoLanguages}
                            data-testid="language-dropdown"
                        />
                    </Box>

                    {/* Text2Text Options */}
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 150, mt: 1, mb: 1 }}
                        data-testid="text2text-options-title"
                    >
                        Text2Text Options
                    </Typography>
                    <Box id="upload-text2text-options" sx={{ my: 2 }} data-testid="text2text-options-container">
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Upload an English SRT file to generate subtitles in other Western languages. Will
                                    generate empty files for Gujarati and Hindi.
                                </Typography>

                                <Paper variant="outlined" sx={{ p: 0.5, borderRadius: 1 }}>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        fullWidth
                                        sx={{ py: 1.5, textTransform: 'none' }}
                                        data-testid="srt-file-upload-btn"
                                    >
                                        {subtitleFile ? `Selected: ${subtitleFile.name}` : 'Upload SRT File'}
                                        <input
                                            type="file"
                                            accept=".srt,.vtt"
                                            hidden
                                            onChange={handleSubtitleFileChange}
                                            data-testid="srt-file-input"
                                        />
                                    </Button>

                                    {/* Show file details when a file is selected */}
                                    {subtitleFile && (
                                        <Box sx={{ mt: 0.25, p: 0.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                                            <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                                                <CheckCircleIcon
                                                    fontSize="small"
                                                    sx={{ mr: 1, verticalAlign: 'middle' }}
                                                />
                                                File Ready: {subtitleFile.name} | Size:{' '}
                                                {(subtitleFile.size / 1024).toFixed(2)} KB | Type:{' '}
                                                {subtitleFile.name.split('.').pop().toUpperCase()}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', mt: 1 }}
                                    >
                                        {subtitleFile
                                            ? 'English Subtitles will be translated to other western languages. For Gujarati and Hindi use, Text2Text Dialog'
                                            : 'Supported formats: SRT, VTT files'}
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Advanced Options */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 1 }}>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 150 }}
                            data-testid="advanced-options-title"
                        >
                            Advanced Options
                        </Typography>
                    </Box>

                    <Grid id="upload-advanced-options" container spacing={2} data-testid="developer-tools-container">
                        {/* Basic options - always visible, now in a single row */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 6 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            id="skipTranslationIsChecked"
                                            checked={skipTranslationIsChecked}
                                            onChange={(e) => setSkipTranslationIsChecked(e.target.checked)}
                                            data-testid="skip-translation-checkbox"
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <Typography variant="body2">Generate Empty SRT files</Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                Will generate empty SRT files for selected languages
                                            </Typography>
                                        </Box>
                                    }
                                />

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            id="onlyTransliterationIsChecked"
                                            checked={onlyTransliterationIsChecked}
                                            onChange={(e) => setOnlyTransliterationIsChecked(e.target.checked)}
                                            data-testid="only-transliteration-checkbox"
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <Typography variant="body2">Only Generate Gujrati SRT</Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                Will generate Gujarati SRT, and an empty English SRT
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    <TaskFieldsSection
                        value={taskFields}
                        onChange={setTaskFields}
                        contentType="VIDEO"
                        defaultLanguages={videoLanguages}
                        singleFile
                    />
                </DialogContent>

                <DialogActions sx={{ p: 2, pt: 1.5 }}>
                    <Button onClick={handleClose} color="inherit" data-testid="cancel-upload-btn">
                        Cancel
                    </Button>
                    <Button
                        id="upLoadButton"
                        variant={subtitleFile ? 'contained' : 'outlined'} // Change to contained when subtitle file is present
                        color="primary"
                        disabled={
                            (googleLinkURL === '' && youtubeLinkURL === '' && !isFileSelected) ||
                            videoName === '' ||
                            !!videoNameError ||
                            (linkType === 'gdrive' && !driveLinkVerified) ||
                            isLinkError ||
                            (taskFields.track && !!contentFieldsError(taskFields))
                        }
                        onClick={() => {
                            handleVideoUpload();
                            if (!videoNameError) {
                                setOpen(false);
                            }
                        }}
                        data-testid="confirm-upload-btn"
                    >
                        Upload
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Path selector dialog */}
            <PathSelectorDialog
                open={pathSelectorOpen}
                onClose={() => setPathSelectorOpen(false)}
                onSelect={handlePathSelected}
                currentPath={selectedPath}
                title="Select Destination Folder"
                data-testid="path-selector-dialog"
            />
            <HelpOverlay
                helpItems={uploadVideoDialogHelpItems}
                title="New Video Guide"
                showButton={false}
                open={helpOpen}
                onClose={() => setHelpOpen(false)}
            />
        </>
    );
};

export default UploadVideoDialog;
